/**
 * Question Controller Component
 *
 * Displays the current question and provides host controls.
 */

import type { LiveQuizQuestion, LiveQuizStatus } from '../../../types/liveQuiz';

interface QuestionControllerProps {
  question: LiveQuizQuestion | Omit<LiveQuizQuestion, 'correctAnswer'>;
  questionIndex: number;
  totalQuestions: number;
  timeRemaining: number;
  timeTotal: number;
  status: LiveQuizStatus;
  answeredCount: number;
  totalParticipants: number;
  correctAnswer?: unknown; // Passed separately from question_end message
  onReveal: () => void;
  onNext: () => void;
  onSkip: () => void;
}

export function QuestionController({
  question,
  questionIndex,
  totalQuestions,
  timeRemaining: _timeRemaining,
  timeTotal: _timeTotal,
  status,
  answeredCount,
  totalParticipants,
  correctAnswer: correctAnswerProp,
  onReveal,
  onNext,
  onSkip,
}: QuestionControllerProps) {
  // timeRemaining and timeTotal are passed for potential future use
  // Timer display is handled by parent component
  void _timeRemaining;
  void _timeTotal;
  const progress = totalParticipants > 0 ? (answeredCount / totalParticipants) * 100 : 0;
  const isRevealed = status === 'reveal';

  return (
    <div className="bg-white/5 rounded-2xl p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="px-3 py-1 bg-purple-500/30 text-purple-300 rounded-full text-sm font-medium">
            Question {questionIndex + 1} of {totalQuestions}
          </span>
          {question.points > 1 && (
            <span className="px-3 py-1 bg-yellow-500/30 text-yellow-300 rounded-full text-sm font-medium">
              {question.points} pts
            </span>
          )}
        </div>

        {status === 'question' && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-purple-300">
              {answeredCount}/{totalParticipants} answered
            </span>
          </div>
        )}
      </div>

      {/* Question Text */}
      <h2 className="text-3xl font-bold text-white mb-8">
        {question.question}
      </h2>

      {/* Question Image */}
      {question.imageUrl && (
        <div className="mb-8">
          <img
            src={question.imageUrl}
            alt="Question"
            className="max-w-full h-auto max-h-64 mx-auto rounded-lg"
          />
        </div>
      )}

      {/* Answer Options (for choice questions) */}
      {question.options && (
        <div className="grid grid-cols-2 gap-4 mb-8">
          {question.options.map((option, index) => {
            // correctAnswer may be option.id or option.value - check both
            // Use prop if provided, otherwise try question.correctAnswer (for full question objects)
            const theCorrectAnswer = correctAnswerProp ?? ('correctAnswer' in question ? question.correctAnswer : undefined);
            const isCorrect = isRevealed && theCorrectAnswer !== undefined &&
              (theCorrectAnswer === option.id || theCorrectAnswer === option.value);

            return (
              <div
                key={option.id}
                className={`p-4 rounded-xl transition-all ${
                  isCorrect
                    ? 'bg-green-500 ring-4 ring-green-300'
                    : `bg-${option.color}-500/20 border border-${option.color}-500/40`
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white ${
                    isCorrect ? 'bg-green-600' : `bg-${option.color}-500`
                  }`}>
                    {String.fromCharCode(65 + index)}
                  </span>
                  <span className={`font-medium ${isCorrect ? 'text-white' : 'text-gray-200'}`}>
                    {option.label}
                  </span>
                  {isCorrect && (
                    <svg className="w-6 h-6 text-white ml-auto" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Progress Bar */}
      {status === 'question' && (
        <div className="mb-6">
          <div className="h-3 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Explanation (after reveal) */}
      {isRevealed && 'explanation' in question && question.explanation && (
        <div className="p-4 bg-blue-500/20 border border-blue-500/30 rounded-xl mb-6">
          <h4 className="text-sm font-semibold text-blue-300 uppercase tracking-wide mb-1">
            Explanation
          </h4>
          <p className="text-blue-100">{question.explanation}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-center gap-4">
        {status === 'question' && (
          <>
            <button
              onClick={onSkip}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors"
            >
              Skip Question
            </button>
            <button
              onClick={onReveal}
              className="px-8 py-3 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl transition-colors"
            >
              Reveal Answer
            </button>
          </>
        )}

        {status === 'reveal' && (
          <button
            onClick={onNext}
            className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold rounded-xl transition-colors"
          >
            {questionIndex + 1 < totalQuestions ? 'Next Question' : 'Show Final Results'}
          </button>
        )}
      </div>
    </div>
  );
}

export default QuestionController;
